import React, { useState, useRef, useEffect } from 'react';
import { Dialog, DialogContent } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { loginByVerifyCode, sendVerifyCode } from '../api/user';
import { IUserInfo } from '../model/user';

interface LoginDialogProps {
  open: boolean;
  onClose: () => void;
  onLogin?: (id: string, info: IUserInfo) => void;
  handleCredentialResponse: (args: any[]) => any;
}

// Email validation regex
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function LoginDialog({
  open,
  onClose,
  onLogin: onLoginProp,
  handleCredentialResponse,
}: LoginDialogProps) {
  // const { sendVerificationCode, verifyCode, login } = useAuth();
  const [step, setStep] = useState<'email' | 'code'>('email');
  const loadGoogleRef = useRef<number>(0);
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [verificationCode, setVerificationCode] = useState([
    '',
    '',
    '',
    '',
    '',
    '',
  ]);
  const [isLoading, setIsLoading] = useState(false);
  const [codeError, setCodeError] = useState('');
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setTimeout(() => {
        setStep('email');
        setEmail('');
        setEmailError('');
        setVerificationCode(['', '', '', '', '', '']);
        setCodeError('');
      }, 200);
    }
  }, [open]);

  const validateEmail = (email: string): boolean => {
    if (!email) {
      setEmailError('');
      return false;
    }
    if (!EMAIL_REGEX.test(email)) {
      setEmailError('Invalid email format');
      return false;
    }
    setEmailError('');
    return true;
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmail(value);
    // 清除错误信息,但不立即验证
    if (value && !EMAIL_REGEX.test(value)) {
      setEmailError('email address is invalid');
    } else {
      setEmailError('');
    }
  };

  const handleGoogleLogin = async () => {
    // 显示一键登录提示框
    if ((window as any).google) {
      (window as any).google.accounts.id.prompt();
    } else {
      toast.info('please wait');
    }
  };

  const handleEmailContinue = async () => {
    // 在点击按钮时进行验证
    if (!email) {
      setEmailError('Please enter your email address');
      return;
    }

    if (!EMAIL_REGEX.test(email)) {
      setEmailError('Invalid email format');
      return;
    }

    setIsLoading(true);
    setEmailError('');
    try {
      // await sendVerificationCode(email);
      await sendVerifyCode(email);
      setStep('code');
    } catch (error: any) {
      if (error.message.includes('Too many requests')) {
        setEmailError(error.message);
      } else {
        setEmailError('Failed to send verification code. Try again later.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleCodeChange = (index: number, value: string) => {
    // Only allow digits
    if (value && !/^\d$/.test(value)) {
      return;
    }

    const newCode = [...verificationCode];
    newCode[index] = value;
    setVerificationCode(newCode);
    setCodeError('');

    // Auto-focus next input - 使用 requestAnimationFrame 确保 DOM 更新完成
    if (value && index < 5) {
      requestAnimationFrame(() => {
        const nextInput = inputRefs.current[index + 1];
        if (nextInput) {
          nextInput.focus();
          nextInput.select();
        }
      });
    }

    // Auto-verify when all fields are filled
    if (newCode.every((digit) => digit !== '') && index === 5) {
      handleVerify(newCode);
    }
  };

  const handleCodeKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (e.key === 'Backspace' && !verificationCode[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleCodePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').trim();

    // Check if pasted data is exactly 6 digits
    if (/^\d{6}$/.test(pastedData)) {
      const newCode = pastedData.split('');
      setVerificationCode(newCode);
      setCodeError('');
      // Focus last input
      inputRefs.current[5]?.focus();
      // Auto-verify
      handleVerify(newCode);
    }
  };

  const handleVerify = async (code?: string[]) => {
    const codeToVerify = code || verificationCode;
    const codeString = codeToVerify.join('');

    if (codeString.length !== 6) {
      return;
    }

    setIsLoading(true);
    try {
      const success = await loginByVerifyCode(email, codeString);
      if (success) {
        if (onLoginProp) {
          // LocalStorageUtil.setLocalStorage(E_LocalStorageKey.userDeviceId, success.jwt_token)
          onLoginProp(success.jwt_token, success.user);
        }
        toast.success('Login successful.');
        onClose();
      }
    } catch (error: any) {
      if (error.message.includes('Code expired')) {
        setCodeError('Code expired');
      } else if (error.message.includes('Incorrect code')) {
        setCodeError('Incorrect code');
      } else {
        setCodeError('Verification failed. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToLogin = () => {
    setStep('email');
    setVerificationCode(['', '', '', '', '', '']);
    setCodeError('');
  };

  const isEmailEntered = email.trim().length > 0;
  const isCodeComplete = verificationCode.every((digit) => digit !== '');

  const loadGoogleBtn = () => {
    console.log(
      'load google btn',
      (window as any).google,
      document.getElementById('google-login-button'),
    );
    if (!document.getElementById('google-login-button')) {
      return;
    }
    if (!(window as any).google) {
      return;
    }
    // if (isGooleLoaded) {
    //   return
    // }
    (window as any).google.accounts.id.initialize({
      client_id:
        '738111955248-qesuofaismj2b7bnoltlmvd9qtqct691.apps.googleusercontent.com',
      callback: handleCredentialResponse, // 登录成功回调函数
      cancel_on_tap_outside: true, // 点击外部是否关闭弹窗
      // auto_select: false, // 是否开启自动登录
      // button_auto_select: false,
      use_fedcm_for_prompt: true,
    });

    // 渲染“使用 Google 登录”按钮
    (window as any).google.accounts.id.renderButton(
      document.getElementById('google-login-button'),
      {
        theme: 'outline',
        size: 'large',
        locale: 'en-US',
        width: '360',
        text: 'continue_with',
        shape: 'rectangular',
        hl: 'en',
      },
    );
    // isGooleLoaded = true
  };
  useEffect(() => {
    // onload执行表明window.google存在
    window.onload = () => loadGoogleBtn();
    // 此方法执行表明btn Dom存在
    console.log('load');
    loadGoogleBtn();
  }, []);
  useEffect(() => {
    if (open && step == 'email') {
      clearTimeout(loadGoogleRef.current);
      loadGoogleRef.current = window.setTimeout(() => {
        loadGoogleBtn();
        clearTimeout(loadGoogleRef.current);
      }, 300);
    }
  }, [open, step]);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        className="sm:max-w-[440px] p-0 gap-0 border-0 shadow-[0px_25px_50px_-12px_rgba(0,0,0,0.25)] overflow-hidden"
        style={{ borderRadius: '20px' }}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none"
        >
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </button>

        <div
          className="p-8"
          style={{
            display: step == 'email' ? 'block' : 'none',
          }}
        >
          {/* Logo & Title */}
          <div className="flex flex-col gap-2 mb-8">
            <div className="flex items-center justify-center h-[32px]">
              <img
                src="https://cdn.tiantiantiaosheng.com/dataupload/tool/2025-11-10/e2ef04e5-bcf1-419f-add1-ec162c73e0bf/062d8cea-d696-4f4f-b1c8-bc0dce4f81a1.png"
                alt="SolveClip Logo"
                className="h-[30px]"
                style={{ width: '130px', height: '30px' }}
              />
            </div>
            <p
              className="text-[14px] leading-[20px] text-center tracking-[-0.1504px]"
              style={{
                color: '#6A7282',
                fontFamily: 'Inter, sans-serif',
                fontWeight: 400,
              }}
            >
              Log in to unlock more clips generation times
            </p>
          </div>

          {/* Google Login Button */}
          <div
            style={{
              display: step == 'email' ? 'block' : 'none',
              width: '360px',
            }}
            id="google-login-button"
            className="mx-auto"
            onClick={() => handleGoogleLogin}
          ></div>

          {/* Divider */}
          <div className="relative my-6">
            <div className="absolute h-px w-full bg-[rgba(0,0,0,0.1)]" />
            <div className="relative flex justify-center">
              <span className="bg-white px-3 text-[14px] text-[#99a1af] tracking-[-0.1504px]">
                or
              </span>
            </div>
          </div>

          {/* Email Input */}
          <div className="mb-6">
            <Input
              type="email"
              placeholder="Enter your email..."
              value={email}
              onChange={handleEmailChange}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && isEmailEntered && !isLoading) {
                  handleEmailContinue();
                }
              }}
              disabled={isLoading}
              className="h-12 rounded-[14px]! bg-gray-50! border! border-gray-200! focus:border-gray-300! px-4 text-[14px] tracking-[-0.1504px] placeholder:text-[#717182]"
            />
            {emailError && (
              <p className="text-red-500 text-xs mt-1">{emailError}</p>
            )}
          </div>

          {/* Continue Button */}
          <Button
            onClick={handleEmailContinue}
            disabled={!isEmailEntered || isLoading}
            className="w-full h-12 rounded-[14px]! text-[14px] font-medium tracking-[-0.1504px] transition-colors opacity-100!"
            style={{
              backgroundColor:
                isEmailEntered && !isLoading ? '#000000' : '#E5E7EB',
              color: isEmailEntered && !isLoading ? '#FFFFFF' : '#99A1AF',
              cursor: !isEmailEntered || isLoading ? 'not-allowed' : 'pointer',
            }}
          >
            Continue
          </Button>

          {/* Footer Note */}
          <p
            className="text-center text-[12px] leading-[16px] mt-6"
            style={{
              color: '#99A1AF',
              fontFamily: 'Inter, sans-serif',
              fontWeight: 400,
            }}
          >
            New users will be registered automatically.
          </p>
        </div>
        <div
          className="p-8"
          style={{
            display: step == 'code' ? 'block' : 'none',
          }}
        >
          {/* Logo & Title */}
          <div className="flex flex-col gap-2 mb-8">
            <div className="flex items-center justify-center h-8">
              <img
                src="https://cdn.tiantiantiaosheng.com/dataupload/tool/2025-11-10/e2ef04e5-bcf1-419f-add1-ec162c73e0bf/062d8cea-d696-4f4f-b1c8-bc0dce4f81a1.png"
                alt="SolveClip Logo"
                className="h-[30px]"
                style={{ width: '130px', height: '30px' }}
              />
            </div>
            <p
              className="text-[14px] leading-5 text-center tracking-[-0.1504px]"
              style={{
                color: '#6A7282',
                fontFamily: 'Inter, sans-serif',
                fontWeight: 400,
              }}
            >
              Enter the code generated from the link sent to
            </p>
            <p
              className="text-[14px] leading-5 text-center tracking-[-0.1504px]"
              style={{
                color: '#364153',
                fontFamily: 'Inter, sans-serif',
                fontWeight: 500,
              }}
            >
              {email}
            </p>
          </div>

          {/* Verification Code Inputs */}
          <div className="flex justify-center gap-2 mb-8">
            {verificationCode.map((digit, index) => (
              <Input
                key={index}
                ref={(el) => {
                  inputRefs.current[index] = el;
                }}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(e) => handleCodeChange(index, e.target.value)}
                onKeyDown={(e) => handleCodeKeyDown(index, e)}
                onPaste={index === 0 ? handleCodePaste : undefined}
                disabled={isLoading}
                className="w-12 h-14 text-center text-[14px] font-semibold rounded-[14px]! border! bg-[#F3F3F5]! tracking-[-0.1504px]"
                style={{ borderColor: '#E5E7EB' }}
              />
            ))}
          </div>

          {codeError && (
            <p className="text-red-500 text-xs text-center mb-4">{codeError}</p>
          )}

          {/* Verify Button */}
          <Button
            onClick={() => handleVerify()}
            disabled={!isCodeComplete || isLoading}
            className="w-full h-12 rounded-[14px]! text-[14px] font-medium tracking-[-0.1504px] mb-4 opacity-100! transition-colors"
            style={{
              backgroundColor:
                isCodeComplete && !isLoading ? '#000000' : '#E5E7EB',
              color: isCodeComplete && !isLoading ? '#FFFFFF' : '#99A1AF',
              cursor: !isCodeComplete || isLoading ? 'not-allowed' : 'pointer',
            }}
          >
            Verify
          </Button>

          {/* Back Button */}
          <Button
            onClick={handleBackToLogin}
            disabled={isLoading}
            variant="ghost"
            className="w-full h-12 rounded-[14px]! text-[14px] hover:text-gray-900 hover:bg-gray-100 tracking-[-0.1504px]"
            style={{
              color: '#4A5565',
              fontFamily: 'Inter, sans-serif',
              fontWeight: 500,
            }}
          >
            Back to Login
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
